import React, { memo, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import classNames from 'classnames';
import { reorderForDragAndDrop } from 'shared-modules/services';
import { useManualTradeChangeActiveCurrency } from 'shared-modules/services/hooks';
import { useAccountInfo } from 'shared-modules/hooks';
import {
  getServiceId,
  isYenConversionInstrument,
  makeAccountExistQueryString,
  makeMaintenanceQueryString,
} from 'shared-modules/utils';
import { FX, TRADE_METHODS } from 'shared-modules/constants';
import { ORDER_TYPES } from 'shared-modules/constants/manualTrade';
import {
  addSuffixInstrumentId,
  removeSuffix,
  transformInstrumentId,
  transformInstrumentList,
} from 'shared-modules/hooks/symbol';
import { openManualTradeCurrencySettings, updateDisplayingTradeCurrencyPair } from '../../../../redux/actions';
import { CURRENCY_PAIRS, MANUAL_TRADE_CURRENCY_PAIRS_POPUP_PARAMS, POPUP_MESSAGES } from '../../../../constants';
import { Spin } from '../../../../components';
import IconButton from '../../../../components/IconButton';
import SettingsIcon from '../../../../components/SettingsIcon';
import { StrictModeDroppable } from '../../../CurrencyPairPage/StrictModeDroppable';
import { CurrencyPairItems } from '../CurrencyPairItems';
import styles from './currencyPair.module.scss';

const CurrencyPair = () => {
  const isLoading = useSelector((state) => state.currencies.ratesIsLoading);

  const dispatch = useDispatch();
  const currencyPairsPopup = useRef({});

  const serviceId = useSelector((state) => state.auth.serviceId);
  const selectedOrderType = useSelector((state) => state.manualTrade.selectedOrderType);
  const isTradeAP = selectedOrderType === ORDER_TYPES.TRADE_AP.id;
  const instrumentList = useSelector((state) => state.settings.instrumentList);
  const displayedInstruments = useSelector((state) => state.settings[serviceId].displayedInstruments);
  const tradeMethod = isTradeAP ? TRADE_METHODS.AP.ID : TRADE_METHODS.MANUAL.ID;
  const transformedInstruments = transformInstrumentList(tradeMethod, displayedInstruments, serviceId);

  const accountInfo = useAccountInfo();
  const isFX = serviceId === FX;
  const currencyInstrumentId = (instrumentId, insrumentServiceId) => {
    if (insrumentServiceId !== FX) {
      return removeSuffix(instrumentId);
    }
    return instrumentId;
  };

  const manualChangeActiveCurrency = useManualTradeChangeActiveCurrency();
  const changeActiveCurrency = useCallback(
    (instrumentId) => {
      // AP表示時であっても、MANUALのIDで更新する
      const instrumentIdM = transformInstrumentId(TRADE_METHODS.MANUAL.ID, instrumentId, serviceId);
      manualChangeActiveCurrency(instrumentIdM);
    },
    [manualChangeActiveCurrency, serviceId],
  );

  const onDragEnd = useCallback(
    (result) => {
      if (!result.destination) {
        return;
      }
      if (result.destination.index === result.source.index) {
        return;
      }
      dispatch(
        updateDisplayingTradeCurrencyPair({
          array: reorderForDragAndDrop(displayedInstruments, result.source.index, result.destination.index),
          serviceId,
        }),
      );
    },
    [displayedInstruments, dispatch, serviceId],
  );
  useEffect(() => {
    if (currencyPairsPopup.current && currencyPairsPopup.current?.postMessage) {
      currencyPairsPopup.current.postMessage(
        {
          message: POPUP_MESSAGES.GET_DISPLAYED_CURRENCY_LIST_SUCCESS,
          payload: { displayedInstruments, serviceId },
        },
        '*',
      );
    }
  }, [displayedInstruments, serviceId]);

  const handleOpenCurrencyPairSettings = useCallback(() => {
    dispatch(openManualTradeCurrencySettings());
  }, [dispatch]);

  const handleOpenCurrencyPairPopup = useCallback(() => {
    const maintenanceQueryString = makeMaintenanceQueryString(accountInfo);
    const accountExistQueryString = makeAccountExistQueryString(accountInfo);
    const url = `/${CURRENCY_PAIRS}?${maintenanceQueryString}&&${accountExistQueryString}`;
    currencyPairsPopup.current = window.open(encodeURI(url), CURRENCY_PAIRS, MANUAL_TRADE_CURRENCY_PAIRS_POPUP_PARAMS);
  }, [accountInfo]);

  const currencyPairPopupEventListener = useCallback(
    (e) => {
      // checking it's correct window;
      if (!e.isTrusted || !e.source) {
        return;
      }
      try {
        if (e.source.name !== CURRENCY_PAIRS) {
          return;
        }
      } catch {
        // 画面遷移後に実行されてしまった場合、DOMException が発生する。
        // ポップアップウィンドウの場合でも遷移時に閉じられてしまうため、後続の処理を行う必要はない。
        return;
      }
      switch (e.data.message) {
        case POPUP_MESSAGES.GET_DISPLAYED_CURRENCY_LIST_REQUEST: {
          currencyPairsPopup.current.postMessage(
            {
              message: POPUP_MESSAGES.GET_DISPLAYED_CURRENCY_LIST_SUCCESS,
              payload: { displayedInstruments, serviceId },
            },
            '*',
          );
          break;
        }
        case POPUP_MESSAGES.CHANGE_DISPLAYED_CURRENCY: {
          changeActiveCurrency(e.data.payload);
          break;
        }
        default: {
          // empty
        }
      }
    },
    [currencyPairsPopup, displayedInstruments, serviceId, changeActiveCurrency],
  );

  const sendMessageClosePopup = useCallback(() => {
    if (currencyPairsPopup.current && currencyPairsPopup.current?.postMessage) {
      currencyPairsPopup.current.postMessage({ message: POPUP_MESSAGES.CLOSE_POPUP }, '*');
    }
  }, []);

  useEffect(() => {
    window.addEventListener('message', currencyPairPopupEventListener);
    window.addEventListener('beforeunload', sendMessageClosePopup);
    return () => {
      window.removeEventListener('message', currencyPairPopupEventListener);
      window.removeEventListener('beforeunload', sendMessageClosePopup);
    };
  }, [sendMessageClosePopup, currencyPairPopupEventListener]);
  useEffect(() => {
    return () => {
      sendMessageClosePopup();
    };
  }, [sendMessageClosePopup]);

  const getCurrencyPairItem = (isYenConversion, instrumentId) => {
    if (!isYenConversion) {
      return CurrencyPairItems[getServiceId(instrumentId, instrumentList)];
    }
    // HKD/JPYはFXに存在しないが、コンポーネントとしてはFXを使用する(円転用銘柄は基本的にFXのコンポーネントを使用する)
    return CurrencyPairItems[getServiceId(addSuffixInstrumentId(instrumentId), instrumentList) || FX];
  };

  const title = `銘柄${isTradeAP ? '（自動売買）' : '（マニュアル）'}`;

  return (
    <div className={styles.wrapper}>
      {isLoading ? (
        <Spin className={styles.loader} />
      ) : (
        <>
          <div className={styles.headerRow}>
            <div className={styles.headerContainer}>
              <IconButton iconName="zoom_out_map" onClick={handleOpenCurrencyPairPopup} />
              <div className={styles.header}>{title}</div>
            </div>
            <SettingsIcon onClick={handleOpenCurrencyPairSettings} />
          </div>
          <div className={styles.tableHeaderRow}>
            <div className={styles.tableTitle}>BID</div>
            <div className={styles.tableTitle}>SPREAD</div>
            <div className={classNames(styles.tableTitle, styles.askTitle)}>ASK</div>
          </div>
          <DragDropContext onDragEnd={onDragEnd}>
            <StrictModeDroppable droppableId="currencies-area">
              {(provided) => (
                <div
                  className={styles.listContainer}
                  ref={provided.innerRef}
                  {...provided.droppableProps} // eslint-disable-line
                >
                  {transformedInstruments.reduce((acc, item, index) => {
                    // 円転用銘柄かどうかをチェック
                    const currencyInstrument = currencyInstrumentId(item.instrumentId, serviceId);
                    const isYenConversion = isYenConversionInstrument(currencyInstrument, serviceId);
                    const CurrencyPairItem = getCurrencyPairItem(isYenConversion, item.instrumentId);
                    if (item.displayed && CurrencyPairItem) {
                      acc.push(
                        <Draggable key={item.instrumentId} draggableId={item.instrumentId} index={index}>
                          {(draggableProvided) => {
                            return (
                              <CurrencyPairItem
                                instrumentId={
                                  isYenConversion && !isFX ? `${serviceId}.${currencyInstrument}` : item.instrumentId
                                }
                                draggableProps={draggableProvided}
                                innerRef={draggableProvided.innerRef}
                                changeActiveCurrency={changeActiveCurrency} // disabled の場合は呼び出されない
                                disabled={isYenConversion}
                                tradeMethod={tradeMethod}
                              />
                            );
                          }}
                        </Draggable>,
                      );
                    }
                    return acc;
                  }, [])}
                  {provided.placeholder}
                </div>
              )}
            </StrictModeDroppable>
          </DragDropContext>
        </>
      )}
    </div>
  );
};

export default memo(CurrencyPair);
